import { useEffect, useRef } from "react";
import { useAuthentication } from "@iad-os/react-ghost-auth";
import { useConfigStore } from "../store/configStore.js";

// Riceve in push (SSE) gli eventi di modifica dati WhatsApp (messaggi/code) e invoca
// `onEvent(kind)`. Permette di aggiornare le pagine in tempo reale senza polling.
// L'auth usa il bearer token: lo stream è consumato via fetch (EventSource non può
// inviare header Authorization).

export type WhatsappEventKind = "messages" | "queues";

const EVENTS_PATH = "/api/v1/whatsapp/messages/events";

export function useWhatsappEvents(onEvent: (kind: WhatsappEventKind) => void): void {
  const config = useConfigStore((s) => s.config);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { token } = useAuthentication() as any;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    const baseUrl = config?.metadata.endpoint_api ?? "";
    const accessToken: string | undefined = token?.access_token;
    if (!baseUrl || !accessToken) return;

    const ctrl = new AbortController();
    let stopped = false;

    const connect = async () => {
      while (!stopped) {
        try {
          const res = await fetch(`${baseUrl}${EVENTS_PATH}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: ctrl.signal
          });
          if (!res.ok || !res.body) throw new Error(`status ${res.status}`);
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!stopped) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let sep: number;
            while ((sep = buffer.indexOf("\n\n")) >= 0) {
              const frame = buffer.slice(0, sep);
              buffer = buffer.slice(sep + 2);
              const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
              if (!dataLine) continue; // heartbeat
              try {
                const payload = JSON.parse(dataLine.slice(5).trim());
                if (payload?.kind === "messages" || payload?.kind === "queues") onEventRef.current(payload.kind);
              } catch {
                /* frame non valido */
              }
            }
          }
        } catch {
          if (stopped || ctrl.signal.aborted) return;
        }
        await new Promise((r) => setTimeout(r, 3000));
      }
    };

    void connect();
    return () => {
      stopped = true;
      ctrl.abort();
    };
  }, [config, token]);
}
